/* Profile publishing, brand images, demo profiles
 *
 * Moved verbatim from currencykeep_2026-08-03.html, lines 735-797.
 * The body below is byte-identical to the original; only the import
 * block above and the export block below were added. (Phase 1, Step 2.)
 */

import { CATALOG, GRADES, colState, isCol, isObs, obsState } from './catalog.js'
import { isCust } from './custom.js'
import { denomInner, render } from './render.js'
import { cat, save, state, toast } from './state.js'
import { HAS, storageDel, storageSet } from './storage.js'
import { getSession, getToken } from './auth.js'
import { clearCommunityPhotos, loadCommunityThumbs } from './community-photos.js'
import { decodeDataUrl } from './migrate.js'
import { BRAND_META_KEY, BRAND_PREVIEW_KEY, BRAND_RAW_KEY } from './photo-keys.js'
import { parsePhotoRef } from '../shared/photo-ref.js'
import { qualifierOn } from './memo.js'
import { issueOnRow, yearOf } from './note-filters.js'

/* ---------- community & profile ---------- */
async function loadProfile(){if(!HAS)return;try{const r=await window.storage.get("notevault:profile",false);if(r&&r.value){const p=JSON.parse(r.value);state.username=p.username||"";state.isPublic=!!p.isPublic;state.usernameSetAt=p.usernameSetAt||0;}}catch(e){}}
async function saveProfile(){try{if(HAS)await storageSet("notevault:profile",JSON.stringify({username:state.username,isPublic:state.isPublic,usernameSetAt:state.usernameSetAt}),false);}catch(e){}}
/* ⚠⚠⚠ SEVEN KEYS PER NOTE SINCE 2026-09-21, NOT SIX — `q` is the
 * EPQ/PPQ designation, public on his ruling: *"the EPQ/PPQ setting
 * should show on public profiles, the same way the grade does."*
 *
 * ⚠⚠ IT IS `qualifierOn(h)`, NOT `h.qualifier`, FOR THE SAME REASON THE
 * SCREEN USES IT: a device that has not run the carry-over yet has the
 * letters in `memo` and `false` in the column, and publishing the raw
 * column would show a stranger OFF while the owner's own screen shows
 * ON. ⚠ The memo itself stays private and is NOT projected — only the
 * boolean the read produces. **This is the one place the compatibility
 * read crosses to another person's screen, so it is the one that has to
 * be retired last.**
 *
 * ⚠ THE OTHER PRODUCER IS `rowToPublicNote` (remote.js) AND IT CARRIES
 * `q` TOO. Two producers of one shape is the standing hazard here; they
 * are named in each other's comments and `pubnote-check` compares the
 * key sets rather than trusting either. */
function snapshot(){return {username:state.username,notes:state.collection.map(h=>({c:h.catId,g:h.grade,y:h.year||"",hid:h.id,hasF:!!h.hasFront,hasB:!!h.hasBack,q:qualifierOn(h)})),updated:Date.now()};}
async function publish(){if(!HAS||!state.username||!state.isPublic)return;try{await storageSet("notevault:pub:"+state.username,JSON.stringify(snapshot()),true);}catch(e){console.error(e);}loadPublic();}
async function unpublish(name){if(!HAS)return;try{await storageDel("notevault:pub:"+(name||state.username),true);}catch(e){}loadPublic();}
function republish(){if(state.isPublic&&state.username)publish();}

/* ⚠⚠ `getPubPhoto` AND `pubPhotoCache` WERE DELETED HERE — Phase 4 Step 7
 * bullet 3, 2026-08-28. A stranger's Community photographs are read from
 * the batch route by `community-photos.js`, proven in production the day
 * before. This function read `notevault:pubph:<user>:<hid>:<slot>`, which
 * `remote.js` routes to LOCAL IndexedDB, so it answered `null` for every
 * stranger who ever called it and did so for a year. Nothing replaced it
 * in this file: the read moved out of the module entirely. */

/* ══ ⚠⚠⚠ BRAND IMAGES — THE CLIENT HALF. PHASE 5, 2026-09-07, WITH JESSE
 *        PRESENT FOR THE FIRST REAL WRITE ON HIS ROW ═══════════════════
 *
 * HIS PRODUCT RULE, VERBATIM: *"pick a logo or banner, see it, everywhere,
 * from then on. No 'this device only'. No error a collector can see."*
 *
 * Until this date `putBrand` wrote a baked JPEG data URL to LOCAL
 * IndexedDB under the username and `getBrand` read it back — so a logo
 * was visible on the device that picked it and nowhere else, and the
 * toast said so honestly ("— this device only", 2026-08-29). The server
 * half — `handleBrandUpload`, the per-slot PNG/JPEG contract, the batch
 * route serving `brand/` objects — was proven and deployed 2026-09-07
 * with NOTHING calling it. This is what calls it.
 *
 * ── THE SHAPE, AND WHY EACH PIECE IS WHERE IT IS ─────────────────────
 *
 *   putBrand         stores the ORIGINAL file, the editor's transform and
 *                    a preview locally (three keys, `photo-keys.js`),
 *                    renders the preview at once, toasts "Logo saved",
 *                    and fires the upload without awaiting it.
 *   uploadBrand      POSTs the original to `/api/photos/upload` with
 *                    `subject=brand&slot=<kind>`. On a 200 whose `ref`
 *                    parses as `r2:` it DELETES the three local keys and
 *                    drops the batch cache, so the next render reads the
 *                    row. On anything else it leaves them alone and says
 *                    why on the console — never to the collector.
 *   sweepPendingBrands  runs on every session establishment beside the
 *                    photograph sweep and retries whatever is still local.
 *   hydrateBrands    reads EVERY profile's logo and banner — the
 *                    collector's own and strangers' alike — through the
 *                    batch route by profile id, the route `photo-batch.js`
 *                    already authorises per row. The one exception is a
 *                    slot of the collector's own that is still pending,
 *                    which shows the local preview until it lands.
 *
 * ⚠⚠ "KEEPS THE LOCAL WRITE UNTIL THE UPLOAD LANDS, THEN READS FROM THE
 * REF" — HIS WORDS, AND THE ORDER IS THE SAFETY ARGUMENT. The local copy
 * is deleted only AFTER the row says `r2:`; a failed or refused upload
 * changes nothing the collector can see and is retried on the next boot.
 * There is no state in which the picked image is shown on no device.
 *
 * ⚠⚠⚠ THE ORIGINAL UPLOADS, NOT THE CANVAS. `editor.js` hands over
 * `ED.file` for brand targets for the same reason it does for notes
 * (Step 6): the server encodes from the bytes the collector chose. For a
 * logo that is the whole transparency ruling — a canvas re-encode would
 * hand the encoder a JPEG and the alpha would be gone before the server
 * ever saw it. The preview is the one place the canvas output survives,
 * and for a logo it is PNG with no background baked in.
 *
 * ⚠ NO "this device only" ANYWHERE ON THIS PATH, and `brand-check`
 * asserts the phrase is absent from this module and the community view.
 * It was the honest label for a pipeline that did not exist; it would be
 * a lie about one that does.
 *
 * ⚠ THE OLD `notevault:brand<kind>:<username>` KEYS ARE NOT READ AND NOT
 * DELETED. A logo picked before this date under the "this device only"
 * regime stays in that device's IndexedDB, orphaned, and is not shown —
 * showing it would be showing something nobody else can see, which is
 * the state the rule forbids. Re-picking is the path, and it is the one
 * Jesse takes for the first real write. Recorded in BUILD_NOTES. */

const BRAND_UPLOAD_PATH='/api/photos/upload';
const BRAND_KINDS=['logo','banner'];
/* The tier each slot renders from. A logo is a 54–58 px chip (`thumb`,
 * ≤400 px). A banner spans the screen at ~100 px tall — on a 3× phone
 * that is over a thousand device pixels wide, and `thumb` would be
 * upscaled; `display` (≤2000 px) is the tier that fits. */
const BRAND_TIER={logo:'thumb',banner:'display'};
/* The cache namespace `loadCommunityThumbs` requires. Brand ids are
 * profile ids, unique across the community, so one namespace holds
 * every profile's brand without collision. */
const BRAND_SCOPE='brand';

function ownId(){const s=getSession();return (s&&s.user&&s.user.id)||null;}

async function readPendingBrand(kind,uid=ownId()){
  if(!HAS||!uid)return null;
  try{const r=await window.storage.get(BRAND_PREVIEW_KEY(uid,kind),false);return r&&r.value?r.value:null;}catch(e){return null;}
}

async function putBrand(kind,{preview,original=null,transform=null}={}){
  const uid=ownId();
  if(!uid||!state.username||!BRAND_KINDS.includes(kind)||!preview)return {saved:false};
  try{
    if(HAS){
      /* The preview first — it is what the screen shows in the seconds
       * before the upload lands, and the one of the three a collector
       * would notice missing. */
      await storageSet(BRAND_PREVIEW_KEY(uid,kind),preview,false);
      if(original){
        await storageSet(BRAND_RAW_KEY(uid,kind),await original.arrayBuffer(),false);
        await storageSet(BRAND_META_KEY(uid,kind),JSON.stringify({type:original.type||null,transform:transform||null}),false);
      }else{
        /* No original (the editor's spec was refused — a bug in the
         * editor, logged there). The preview uploads instead, geometry
         * baked, so the collector still gets the image everywhere. */
        await storageDel(BRAND_RAW_KEY(uid,kind),false);
        await storageDel(BRAND_META_KEY(uid,kind),false);
      }
    }
  }catch(e){console.error("brand: could not store the "+kind,e);toast("Image too large to save");return {saved:false};}
  render();toast(kind==="logo"?"Logo saved":"Banner saved");
  /* ⚠ FIRED, NOT AWAITED — the same measurement that shaped the
   * photograph sweep: an upload is seconds, and the collector has
   * already been shown the image. The rejection handler keeps a broken
   * upload from being an invisible one. */
  uploadBrand(kind).catch((e)=>console.error("brand upload failed",e));
  return {saved:true};
}

/* ⚠ `fetchImpl` AND `tokenImpl` ARE INJECTABLE FOR THE GATE ONLY. In the
 * app both default to the real thing. */
async function uploadBrand(kind,{fetchImpl=fetch,tokenImpl=getToken}={}){
  const uid=ownId();
  if(!uid||!HAS)return {ok:false,error:'no_session'};
  const preview=await readPendingBrand(kind,uid);
  if(!preview)return {ok:false,error:'nothing_pending'};

  let raw=null,meta=null;
  try{const r=await window.storage.get(BRAND_RAW_KEY(uid,kind),false);raw=r&&r.value?r.value:null;}catch(e){raw=null;}
  try{const m=await window.storage.get(BRAND_META_KEY(uid,kind),false);meta=m&&m.value?JSON.parse(m.value):null;}catch(e){meta=null;}

  /* ⚠⚠⚠ THE ORIGINAL, WHEN THERE IS ONE. The preview is the fallback
   * for a record with no original, never the first choice — see the
   * header. `brand-check` §2 asserts the body is the original's bytes. */
  let bytes,type,transform=null;
  if(raw&&meta&&meta.type){bytes=raw;type=meta.type;transform=meta.transform||null;}
  else{const d=decodeDataUrl(preview);if(!d.ok)return {ok:false,error:d.error};bytes=d.bytes;type=d.contentType;}

  const token=await tokenImpl();
  if(!token)return {ok:false,error:'no_session'};

  const params=new URLSearchParams({subject:'brand',slot:kind});
  if(transform)params.set('transform',JSON.stringify(transform));

  let res;
  try{
    res=await fetchImpl(`${BRAND_UPLOAD_PATH}?${params}`,{
      method:'POST',
      headers:{authorization:`Bearer ${token}`,'content-type':type},
      body:bytes,
    });
  }catch(e){
    /* Console, not toast. The image is on screen from the local copy and
     * the next boot retries; there is nothing for the collector to do. */
    console.warn(`brand upload: ${kind} — network: ${String(e&&e.message||e)}`);
    return {ok:false,error:'network'};
  }
  let body=null;
  try{body=await res.json();}catch(e){body=null;}
  if(res.status!==200||!body||body.ok!==true){
    console.warn(`brand upload: ${kind} — ${(body&&body.error)||('http_'+res.status)}: ${(body&&body.message)||''}`);
    return {ok:false,error:(body&&body.error)||`http_${res.status}`,status:res.status};
  }
  /* ⚠ THE RESPONSE IS NOT TAKEN AS THE ANSWER — the same rule as
   * `migrateOne`. What the rest of the app reads is the ref on the row,
   * so that is what decides whether the local copy may go. */
  if(parsePhotoRef(body.ref).scheme!=='r2'){
    console.warn(`brand upload: ${kind} — the endpoint answered ok and the ref is ${JSON.stringify(body.ref)}`);
    return {ok:false,error:'ref_not_written'};
  }

  /* ══ LANDED. ONLY NOW does the local copy go, and the cached tier with
   * it — a batch cache still holding the previous logo would show the
   * old image for the rest of the session. */
  try{
    await storageDel(BRAND_PREVIEW_KEY(uid,kind),false);
    await storageDel(BRAND_RAW_KEY(uid,kind),false);
    await storageDel(BRAND_META_KEY(uid,kind),false);
  }catch(e){console.warn("brand upload: landed, but the local copy could not be removed",e);}
  clearCommunityPhotos();
  render();
  return {ok:true,ref:body.ref};
}

/* Runs on every session establishment (`views-auth.js:bootSession`),
 * beside the photograph sweep and for the same reason: "a session exists"
 * and "whatever is still local is on its way" are one event. Silent on
 * success; each failure is one console line from `uploadBrand`. */
async function sweepPendingBrands(opts={}){
  const uid=ownId();
  if(!uid||!HAS)return {ran:false,attempted:0,landed:0,failed:0};
  let attempted=0,landed=0,failed=0;
  for(const kind of BRAND_KINDS){
    if(!(await readPendingBrand(kind,uid)))continue;
    attempted++;
    const r=await uploadBrand(kind,opts);
    if(r.ok)landed++;else failed++;
  }
  return {ran:attempted>0,attempted,landed,failed};
}

/* One batch request per kind for every id on screen. A failed request
 * leaves the chips as initials and says why on the console — a brand
 * image is decoration, and a collector can act on nothing here. */
async function loadBrands(ids,kind){
  const want=[...new Set(ids.filter(Boolean))];
  if(!want.length)return new Map();
  let r;
  try{
    r=await loadCommunityThumbs({username:BRAND_SCOPE,subject:'brand',ids:want,slot:kind,tier:BRAND_TIER[kind],tokenImpl:getToken});
  }catch(e){console.warn("brand: could not load "+kind+"s",e);return new Map();}
  if(r.failed)console.warn(`brand: could not load ${kind}s — ${r.failed.reason}${r.failed.status?' '+r.failed.status:''}`);
  return r.photos;
}
function fillBrand(el,url,kind){
  if(!el||!url)return;
  if(kind==="banner"){el.style.backgroundImage="url("+url+")";el.classList.add("hasimg");}
  else{el.classList.add("hasimg");el.innerHTML='<img alt="logo">';el.firstChild.src=url;}
}
async function hydrateBrands(){
  const uid=ownId();
  if(state.screen==="community"){
    /* Every profile with an id — the demo profiles have none and are not
     * asked about. The collector's own id joins the same request whether
     * or not their profile is public: the batch route reads the base
     * table first, so their own row answers there. */
    const profs=(state.pubProfiles||[]).filter(p=>p&&p.id);
    const ids=profs.map(p=>p.id);
    if(state.username&&uid&&!ids.includes(uid))ids.push(uid);
    const localLogo=state.username&&uid?await readPendingBrand("logo",uid):null;
    const logos=await loadBrands(ids,"logo");
    for(const p of profs){
      const el=document.getElementById("plogo_"+p.username);
      fillBrand(el,(uid&&p.id===uid&&localLogo)||logos.get(p.id),"logo");
    }
    if(state.username&&uid){
      fillBrand(document.getElementById("myLogo"),localLogo||logos.get(uid),"logo");
      const localBanner=await readPendingBrand("banner",uid);
      const banner=localBanner||(await loadBrands([uid],"banner")).get(uid);
      fillBrand(document.getElementById("myBanner"),banner,"banner");
    }
  }
  if(state.screen==="pubprofile"&&state.viewUser){
    const p=(state.pubProfiles||[]).find(x=>x.username===state.viewUser);
    if(p&&p.id){
      const own=!!uid&&p.id===uid;
      const localLogo=own?await readPendingBrand("logo",uid):null;
      const localBanner=own?await readPendingBrand("banner",uid):null;
      const logo=localLogo||(await loadBrands([p.id],"logo")).get(p.id);
      const banner=localBanner||(await loadBrands([p.id],"banner")).get(p.id);
      fillBrand(document.getElementById("phlogo"),logo,"logo");
      fillBrand(document.getElementById("pbanner"),banner,"banner");
    }
  }
}

/* ══ ⚠⚠⚠ `publishAllPhotos` AND `clearPubPhotos` WERE DELETED HERE ═══
 * Phase 4 Step 7 bullet 3, 2026-08-28, with `src/publish-photos.js` and
 * its two gates. **A public profile no longer copies photographs
 * anywhere**: `publish()` sets `is_public`, and a stranger reads the
 * bytes out of R2 through the batch route.
 *
 * ⚠⚠ SO "PRIVATE MEANS HIDDEN" IS NO LONGER ENFORCED BY DELETING BYTES.
 * It is enforced by `public_holdings` refusing the read — `rls-check`'s
 * claim against the real database, which is now load-bearing for a
 * promise to the collector in a way it was not before. That is a
 * deliberate trade and it is recorded in BUILD_NOTES, not just here.
 *
 * ⚠ AND THE DELETE-THEN-CANNOT-RESTORE STOPS EXISTING RATHER THAN BEING
 * FIXED. `946aae7` closed it on 2026-08-26 and was right to; with no
 * local copy to delete, a rename cannot touch a photograph at all —
 * R2 objects are keyed by OWNER ID, not by username. */
const DEMO_SEED=[
  ["greenback_gene",[["16","EF40","1869"],["40","VF20","1862"],["215","F12","1886"],["347","VF20","1890"]]],
  ["silvercert_sue",[["224","CU60","1896"],["236","EF40","1899"],["258","VF20","1899"],["1173","VF20","1907"],["347","EF40","1890"]]],
  ["largenote_lou",[["18","VG8","1869"],["41","F12","1862"],["63","VF20","1907"],["150","EF40","1880"],["215","F12","1886"]]],
];
let DEMO_PROFILES=[];
function buildDemos(){DEMO_PROFILES=DEMO_SEED.map(d=>({username:d[0],demo:true,notes:d[1].map(p=>{const n=CATALOG.find(x=>x.fr===p[0]&&x.section!=="Colonial");return n?{c:n.sid,g:p[1],y:p[2]||""}:null;}).filter(Boolean)})).filter(p=>p.notes.length);}
/* ── ⚠⚠ THIS FUNCTION USED TO FAIL COMPLETELY SILENTLY ───────────────
 * 2026-08-27. Both catches below were bare `catch(e){}`. A community read
 * that threw — no session, offline, a refused token — left `real` empty,
 * and the next line then quietly substituted the DEMO SEED. The screen
 * showed three fictional collectors and said nothing at all.
 *
 * ⚠ THAT IS WHAT HID THE SIGN-IN DEFECT for as long as the feature has
 * existed: every collector's first session showed an empty Community and
 * healed on the next page load. The sequencing fix is in
 * `views-auth.js:bootSession`; this is the reason nobody could have SEEN
 * it. A failure that renders as a plausible screen is the shape this
 * project keeps paying for.
 *
 * ⚠ CONSOLE ONLY, DELIBERATELY. A toast on every failed community read
 * would fire on an ordinary offline browse of a screen nobody is
 * blocked by — unlike a failed vault read, which loses work and does
 * toast (`state.js`, the extract REPLACEMENT). Whether this deserves a
 * visible state of its own is Jesse's call, not a thing to decide by
 * reflex while fixing something else. */
async function loadPublic(){
  let real=[];
  if(HAS){try{const r=await window.storage.list("notevault:pub:",true);
    if(r&&r.keys)for(const k of r.keys){try{const g=await window.storage.get(k,true);if(g&&g.value){const p=JSON.parse(g.value);if(p&&p.username)real.push({id:p.id||null,username:p.username,notes:p.notes||[]});}}catch(e){console.error("community: could not read "+k,e);}}}catch(e){console.error("community: could not list public profiles",e);}}
  const names=new Set(real.map(p=>p.username));
  state.pubProfiles=[...real,...DEMO_PROFILES.filter(d=>!names.has(d.username))];
  if(state.screen==="community"||state.screen==="pubprofile")render();
}
/* ⚠⚠⚠ DEAD CODE WITH A LIVE HAZARD, AND IT IS FLAGGED RATHER THAN
 * DELETED BECAUSE DELETING IT IS JESSE'S CALL, NOT A SIDE EFFECT OF THE
 * QUICK-ADD FIX. Nothing calls this and it is not exported — checked
 * across src/ and scripts/ on 2026-08-29.
 *
 * TWO THINGS ARE WRONG WITH IT AND ONLY ONE IS NEW:
 *
 * 1. NEW, AND MINE. `n.g` is the per-grade price array. A quick-add
 *    entry resolved off `public_holdings` does NOT carry it — db/009
 *    projects four fields and `g` is deliberately not one of them — so
 *    `n.g[...]` on a custom note would throw where it previously
 *    returned null. `n&&n.g?` is the guard; without it this fix plants
 *    a crash in a function that is one import away from running.
 *
 * 2. OLDER, AND THE REASON IT SHOULD PROBABLY GO. It totals the DOLLAR
 *    VALUE of a stranger's collection. The screen's own footer promises
 *    "Public collections show which notes a collector owns — never
 *    values or what anyone paid", and db/002 keeps `cost` and
 *    `user_value` out of the public view by name. Wiring this up would
 *    contradict a promise made in three places. */
function profVal(p){return p.notes.reduce((s,x)=>{const n=x.n||cat(x.c);const v=n&&n.g?n.g[GRADES.indexOf(x.g)]:null;return s+(v||0);},0);}
/* ⚠ STEP 6, STOP B — THREE NAMES, HIS RULINGS 2026-09-27: *"Label the
 * small Nationals option "Small Size National Bank Note" … keep the
 * catalog's section name internal"*; *"Keep "National Gold Bank Note" …
 * "Large Size National Gold Bank Note" stays only as the internal section
 * name"*; *"Split Treasury Note: "Treasury Note" (1812–1861) and
 * "Treasury (Coin) Note" (1890–91), no "Large Size""*. Planned for stop C
 * and moved here: the card's Type pick-list prints every option through
 * this map (one name everywhere, his ruling), so the pick-list could not
 * match the approved mockups without them. */
const TYPE_LABEL={"Legal Tender":"Large Size Legal Tender","Silver Certificate":"Large Size Silver Certificate","Gold Certificate":"Large Size Gold Certificate","Federal Reserve":"Large Size Federal Reserve Note","Treasury / National Bank":"Large Size Treasury / National Bank Note",
  "Small Size National Currency":"Small Size National Bank Note","Large Size National Gold Bank Note":"National Gold Bank Note","Large Size Treasury / Coin Note":"Treasury (Coin) Note"};
function typeLabel(s){return TYPE_LABEL[s]||s;}
function isErr(n){return !!(n&&n.section==="Error Note");}
/* ⚠ STOP C (C2), HIS D OF 2026-09-27 (the round-2 mockups, approved as
 * drawn in round 3's 15): a CUSTOM Obsolete note that carries a state —
 * stop B's no-Fr fill writes the read state to `desc` under *Obsolete
 * Note* — reads like a catalogue one on its card: *"Michigan · $3"* /
 * *"Obsolete · Michigan"*. Without a state it keeps the custom lines. */
const custObsState=n=>isCust(n)&&isObs(n)?obsState(n):"";
/* ⚠ STOP C (C3), HIS ANSWER 6 OF 2026-09-27: the issue in the vault
 * card's sub line — *"WWII Emergency Note · Hawaii · Fr. 2300"* — once
 * the catalogue carries it (`issueOnRow`, note-filters.js: one section,
 * named there). */
/* ⚠ HIS RULING 2026-10-03 (the fifth session, mid-build): the sub line
 * DROPS the issue when the TITLE already shows it — which is exactly when
 * the note has no year (`cardTitle`), so the two lines read from the same
 * `yearOf`. A note with a year keeps the issue here (the WWII "Hawaii"). */
const issueSub=(n,year)=>{const i=issueOnRow(n);return i&&yearOf(year,n)?" · "+i:"";};
function cardTitle(n,year){if(custObsState(n))return custObsState(n)+" · "+n.denom;if(isCust(n))return ((year||"")+(n.denom?(year?" · ":"")+n.denom:""))||"New note";if(isErr(n))return n.fr;if(isCol(n))return n.fr+" · "+n.denom;if(isObs(n))return obsState(n)+" · "+n.denom;
  /* ⚠ HIS RULING 2026-10-03 (the fourth session): a catalogue note's card
   * reads the SAME year the rest of the app shows — `yearOf` (the
   * collector's own year, else the recovered `series`, else the text's
   * year) — and the em dash only when there is none. Before this the card
   * printed the collector's own year or the dash, so Fr 60 ("1917 · …")
   * read "— · $2" above a hero that said 1917. */
  /* ⚠ HIS RULING 2026-10-03 (the fifth session), off the stop D renders: a
   * Fractional note with no year reads its ISSUE in place of the dash
   * ("Third Issue · 50¢"); the dash only when it has neither. `issueOnRow`
   * is the two sections by name (WWII rows all carry a year; 174 of the
   * 176 Fractional rows carry none, 164 carry an issue, 12 carry neither). */
  return (yearOf(year,n)||issueOnRow(n)||"—")+" · "+n.denom;}
function cardSub(n,year){if(custObsState(n))return "Obsolete · "+custObsState(n);if(isCust(n))return typeLabel(n.section)+(n.fr?" · Fr. "+n.fr:"");if(isErr(n))return "Error Note";if(isCol(n))return "Colonial · "+colState(n);if(isObs(n))return "Obsolete · "+obsState(n);return typeLabel(n.section)+issueSub(n,year)+" · Fr. "+n.fr;}
/* ⚠ A PUBLISHED CUSTOM FIELD IS TEXT — his register item of 2026-09-27,
 * built 2026-10-04: a custom entry's `fr` and `denom` are typed by the
 * collector (and since step 6 filled by the reader) and reach strangers
 * through db/009, so they are escaped before `denomInner` wraps them in
 * markup. `$` and `—`, the two characters it tests, are not touched. */
const escH=s=>String(s==null?"":s).replace(/&/g,"&amp;").replace(/</g,"&lt;").replace(/>/g,"&gt;").replace(/"/g,"&quot;");
function chipBody(n){if(isErr(n))return '<b style="font-size:11px;letter-spacing:.5px">ERR</b>';if(isCol(n))return '<b style="font-size:13px">'+escH(n.fr.split("-")[0])+'</b>';return denomInner(escH(n.denom));}

export {
  buildDemos,
  cardSub,
  cardTitle,
  chipBody,
  hydrateBrands,
  isErr,
  loadProfile,
  loadPublic,
  publish,
  putBrand,
  readPendingBrand,
  republish,
  snapshot,
  saveProfile,
  sweepPendingBrands,
  typeLabel,
  unpublish,
  uploadBrand,
}
